import { PointerEvent as ReactPointerEvent, useRef, useState } from 'react';

interface BeforeAfterSliderProps {
  /** Left/original image (revealed on the left of the divider). */
  before: string;
  /** Right/result image (the base layer). */
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
}

/**
 * Draggable before/after comparison. The "after" image is the base layer (it
 * defines the box size); the "before" image is overlaid and revealed up to the
 * divider via `clip-path`. Both use `object-contain` so differing input/output
 * dimensions still line up within the same box.
 */
export function BeforeAfterSlider({
  before,
  after,
  beforeLabel = 'Input',
  afterLabel = 'Output',
}: BeforeAfterSliderProps) {
  const [pos, setPos] = useState(50); // divider position, percent from the left
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  function updateFromClientX(clientX: number) {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }

  function onDown(e: ReactPointerEvent<HTMLDivElement>) {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  }

  function onMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (draggingRef.current) updateFromClientX(e.clientX);
  }

  function onUp(e: ReactPointerEvent<HTMLDivElement>) {
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer capture may already be released; ignore.
    }
  }

  return (
    <div
      ref={containerRef}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      className="relative w-full select-none overflow-hidden rounded-lg border border-slate-200 bg-slate-900 cursor-ew-resize touch-none"
    >
      {/* After image: base layer, defines the box height. */}
      <img src={after} alt={afterLabel} className="block w-full h-auto pointer-events-none" draggable={false} />

      {/* Before image: overlaid, revealed up to the divider. */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={before}
          alt={beforeLabel}
          className="absolute inset-0 h-full w-full object-contain pointer-events-none"
          draggable={false}
        />
      </div>

      {/* Labels */}
      <span className="absolute top-2 left-2 rounded bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white pointer-events-none">
        {beforeLabel}
      </span>
      <span className="absolute top-2 right-2 rounded bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white pointer-events-none">
        {afterLabel}
      </span>

      {/* Divider + handle */}
      <div
        className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.25)] pointer-events-none"
        style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white shadow-md flex items-center justify-center">
          <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l-4 5 4 5m8-10l4 5-4 5" />
          </svg>
        </div>
      </div>
    </div>
  );
}
